// src/pages/shipment/ShipmentList.jsx
import React, { useEffect, useState, useCallback } from "react";
import CommonDataTable from "../../components/CommonDataTable";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { toast } from "../../components/AppToast";
import { handleExportFromBase64 } from "../../utils/ExportTableData";
import { formatToDisplayDate } from "../../utils/dateFormatter";
import { fetchShipment } from "../../services/shipment/shipment";

const defaultVisibleFieldsOLD = [
    "shipmentOrder", // Change from "shipmentNumber" to "shipmentOrder"
    "source",
    "destinationCity",
    "status",
    "carrierName",
    "awbNumber",
    "createdAt"
];

const defaultVisibleFields = [
    "shipmentOrder",
    "loadId",
    "source",
    "systemStatus",
    "wmsStatus",
    "erpStatus",
    "sourceName",
    "destinationName",
    "carrierName",
    "vehicleNumber",
    "actualDepartureDate",
    "expectedArrivalDate",
    "createdAt",
];


// ⬇️ imports ke baad add karo
const EXCLUDED_FIELDS = new Set(["id", "_id", "__raw", "status"]);


const startCase = (str) =>
    (str || "")
        .replace(/([A-Z]+)/g, " $1")
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, c => c.toUpperCase())
        .trim();

/** Align numeric / ERP-style status codes with sales order table (MainTable). */
const statusToLabel = (v) => {
    if (v === 0 || `${v}` === "0") return "NEW";
    if (v === 1 || `${v}` === "1") return "OPEN";
    if (v === 2 || `${v}` === "2") return "LOADED";
    if (v === 7 || `${v}` === "7") return "CLOSED";
    if (v === 8 || `${v}` === "8") return "ON HOLD";
    if (v === 9 || `${v}` === "9") return "SHIPPED";

    if (typeof v === "string" && v.trim() !== "") return v.toUpperCase();
    return "";
};

const normalizeStatusDisplay = (raw) => {
    if (raw === "" || raw == null) return "";
    if (
        typeof raw === "number" ||
        (typeof raw === "string" && /^\d+$/.test(String(raw).trim()))
    ) {
        return statusToLabel(raw);
    }
    return String(raw).trim();
};

const normalizeErpStatusDisplay = (raw) => {
    if (raw === "" || raw == null) return "";
    if (
        typeof raw === "number" ||
        (typeof raw === "string" && /^\d+$/.test(String(raw).trim()))
    ) {
        return statusToLabel(raw);
    }
    const s = String(raw).trim();
    return s !== "" ? s.toUpperCase() : "";
};

export default function ShipmentList({
    onViewShipment,
    onEditShipment,
    refreshKey,
    onConfirmDelivery,
    setShowConfirmButton,
    // New props for create button
    showCreateButton = false,
    createButtonText = "Create New Shipment Order",
    createButtonIcon = "bi-plus-lg",
    onCreateClick,
    // Add data refresh prop
    onDataRefresh
}) {
    const [allPossibleColumns, setAllPossibleColumns] = useState([]);
    const [rows, setRows] = useState([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuRow, setMenuRow] = useState(null);
    const [loading, setLoading] = useState(false);


    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);


    // src/schema/shipmentFieldMap.js (ya jahan bhi rakhna ho)

     const SHIPMENT_FILTER_FIELD_MAP = {
        // Action column (no backend field)
        __actions: null,

        // ⚙️ Special cases / aliases
        // UI: shipmentOrder  → API: shipmentNumber
        shipmentOrder: "shipmentNumber",

        // Direct 1:1 mappings (UI field === API field)
        source: "source",
        destinationCity: "destinationCity",
        systemStatus: "status",
        wmsStatus: "status",
        erpStatus: "erpStatus",
        carrierName: "carrierName",
        awbNumber: "awbNumber",
        createdAt: "createdAt",
        actualArrivalDate: "actualArrivalDate",
        actualDepartureDate: "actualDepartureDate",
        addressLine1: "addressLine1",
        addressLine2: "addressLine2",
        addressLine3: "addressLine3",
        addressLine4: "addressLine4",
        city: "city",
        closeDate: "closeDate",
        contact: "contact",
        country: "country",
        createUsing: "createUsing",
        dateUDF1: "dateUDF1",
        dateUDF2: "dateUDF2",
        dateUDF3: "dateUDF3",
        dateUDF4: "dateUDF4",
        dateUDF5: "dateUDF5",
        destinationName: "destinationName",
        driverDetails: "driverDetails",
        email: "email",
        expectedArrivalDate: "expectedArrivalDate",
        expectedDepartureDate: "expectedDepartureDate",
        expectedReceiptDate: "expectedReceiptDate",
        externalShipmentNumber: "externalShipmentNumber",
        invoiceDate: "invoiceDate",
        invoiceNumber: "invoiceNumber",
        listUDF1: "listUDF1",
        listUDF2: "listUDF2",
        listUDF3: "listUDF3",
        listUDF4: "listUDF4",
        listUDF5: "listUDF5",
        listUDF6: "listUDF6",
        listUDF7: "listUDF7",
        listUDF8: "listUDF8",
        listUDF9: "listUDF9",
        listUDF10: "listUDF10",
        loadId: "loadId",
        name: "name",
        numericUDF1: "numericUDF1",
        numericUDF2: "numericUDF2",
        numericUDF3: "numericUDF3",
        numericUDF4: "numericUDF4",
        numericUDF5: "numericUDF5",
        openQuantity: "openQuantity",
        owner: "owner",
        shipmentDate: "shipmentDate",
        shipmentNumber: "shipmentNumber",
        shippingMode: "shippingMode",
        sourceName: "sourceName",
        state: "state",
        textUDF1: "textUDF1",
        textUDF2: "textUDF2",
        textUDF3: "textUDF3",
        textUDF4: "textUDF4",
        textUDF5: "textUDF5",
        textUDF6: "textUDF6",
        textUDF7: "textUDF7",
        textUDF8: "textUDF8",
        textUDF9: "textUDF9",
        textUDF10: "textUDF10",
        textUDF11: "textUDF11",
        textUDF12: "textUDF12",
        textUDF13: "textUDF13",
        textUDF14: "textUDF14",
        textUDF15: "textUDF15",
        totalOrderedQuantity: "totalOrderedQuantity",
        totalQty: "totalQty",
        totalVolume: "totalVolume",
        totalWeight: "totalWeight",
        vehicleNumber: "vehicleNumber",
    };



// console.log("all possible columns in the Shippmetn List...........>>>>>>>>", allPossibleColumns);

 function mapShipmentUiFiltersToApi(filters = []) {
        if (!Array.isArray(filters)) return [];

        return filters
            .map((f) => {
                if (!f || !f.columnId) return null;

                const apiField = SHIPMENT_FILTER_FIELD_MAP[f.columnId] ?? f.columnId;

                // __actions ya koi bhi null-mapped field skip
                if (apiField === null) return null;

                return {
                    columnId: apiField,  // backend field path
                    operator: f.operator,
                    value: f.value,
                };
            })
            .filter(Boolean);
    }





    const isInternalSource = (row) =>
        String(row?.source ?? row?.sourceLabel ?? "")
            .trim()
            .toLowerCase() === "internal";


    const loadList = useCallback(async (pageNo, pageSizeNo, filter = []) => {
        setLoading(true);
        try {



            const apifilter = mapShipmentUiFiltersToApi(filter)
            const resp = await fetchShipment(pageNo, pageSizeNo, apifilter);


            const apiData = resp.data;
            const total = resp.pagination.total;
            const fieldSet = new Set();
            const builtRows = (apiData || []).map((rec, idx) => {
                const hdr = rec.shipmentDetails ?? rec.header ?? {};
                const src = rec.sourceDetail ?? {};
                const dest = rec.destination ?? {};
                const billTo = rec.billTo ?? {};
                const carr = rec.carrier ?? {};
                const load = rec.load ?? rec.loadDates ?? {};
                const udf = rec.udf ?? {};


                const statusRaw =
                    rec.status ??
                    hdr.status ??
                    null;
                const statusDisplay = normalizeStatusDisplay(statusRaw);

                const erpStatusRaw =
                    hdr.erpStatus ??
                    hdr.erp_status ??
                    rec.erpStatus ??
                    rec.erp_status ??
                    rec.externalOrderStatus ??
                    hdr.externalOrderStatus ??
                    rec.orderStatusErp ??
                    hdr.orderStatusErp ??
                    "";
                const erpStatusDisplay = normalizeErpStatusDisplay(erpStatusRaw);

                const flat = {
                    id: hdr.shipmentNumber || rec._id || idx,
                    shipmentOrder: hdr.shipmentNumber ?? rec.shipmentNumber ?? null, // Change field name for table
                    source: src.sourceName ?? src.name ?? hdr.source ?? null,
                    // source:"INTERNAL",
                    destinationName: dest.name ?? dest.destinationName ?? null,
                    destinationCity: dest.city ?? null,
                    status: statusDisplay || statusRaw,
                    systemStatus: statusDisplay || statusRaw,
                    wmsStatus: statusDisplay || statusRaw,
                    erpStatus: erpStatusDisplay,
                    totalQty: hdr.totalQty ?? hdr.totalOrderedQuantity ?? null,
                    totalWeight: hdr.totalWeight ?? null,
                    totalVolume: hdr.totalVolume ?? null,
                    shipmentDate: hdr.shipmentDate ? formatToDisplayDate(hdr.shipmentDate) : null,
                    createdAt: rec.createdAt
                        ? formatToDisplayDate(rec.createdAt, true, '24h')   // '12h' chahiye to third arg '12h' kar dena
                        : (rec.updatedAt ? formatToDisplayDate(rec.updatedAt, true, '24h') : ""),
                    carrierName: carr.carrierName ?? null,
                    awbNumber: carr.awbNumber ?? null,
                    invoiceNumber: rec.invoice?.invoiceNumber ?? null,
                    invoiceDate: rec.invoice?.invoiceDate ? formatToDisplayDate(rec.invoice.invoiceDate) : null,
                    __raw: rec
                };

                const merged0 = {
                    ...flat,
                    ...hdr,
                    ...src,
                    ...dest,
                    ...billTo,
                    ...carr,
                    ...load,
                    ...udf
                };

                const displaySource = rec.shipmentDetails.source;
                const merged = {
                    ...merged0,
                    source: displaySource,
                    status: statusDisplay || statusRaw,
                    systemStatus: statusDisplay || statusRaw,
                    wmsStatus: statusDisplay || statusRaw,
                    erpStatus: erpStatusDisplay,
                };


                Object.keys(merged).forEach(k => {
                    if (!EXCLUDED_FIELDS.has(k)) fieldSet.add(k);
                });

                return merged;
            });

            // Add the default visible fields
            defaultVisibleFields
                .filter(f => !EXCLUDED_FIELDS.has(f))
                .forEach(f => fieldSet.add(f));


            // Filter out columns with numeric names (0, 1, 2, 3...)
            const filteredFields = Array.from(fieldSet).filter(f => !/^\d+$/.test(f));

            const remaining = filteredFields.filter(f => !defaultVisibleFields.includes(f)).sort((a, b) => a.localeCompare(b));
            const orderedFields = [...defaultVisibleFields, ...remaining];

            const actionsColumn = {
                field: "__actions",
                headerName: "",
                width: 60,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleOpenMenu(e, params.row); }}>
                        <MoreVertIcon fontSize="small" />
                    </IconButton>
                )
            };

            const cols = [
                actionsColumn,
                ...orderedFields
                    .filter(f => !EXCLUDED_FIELDS.has(f))
                    .map(f => ({
                        field: f,
                        headerName:
                            f === "shipmentOrder"
                                ? "Shipment Order"
                                : f === "createdAt"
                                    ? "Created Date"
                                    : f === "systemStatus"
                                        ? "System Status"
                                        : f === "wmsStatus"
                                            ? "WMS Status"
                                            : f === "erpStatus"
                                                ? "ERP Status"
                                                : startCase(f),
                        width: 160
                    }))
            ];


            setAllPossibleColumns(cols);
            setRows(builtRows);
            setTotalCount(total);
        } catch (err) {
            console.error("Error loading shipments", err);
            toast.error(err?.message || "Failed to load shipments");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [/* no deps */]);

    useEffect(() => { loadList(page, pageSize); }, [loadList, page, pageSize]);

    const handleOpenMenu = (e, row) => { e.stopPropagation(); setMenuAnchorEl(e.currentTarget); setMenuRow(row); };
    const handleCloseMenu = () => { setMenuAnchorEl(null); setMenuRow(null); };

    const handleView = () => {
        setShowConfirmButton(false);
        if (!menuRow) return;
        const raw = menuRow.__raw || menuRow;
        if (onViewShipment) onViewShipment(raw);
        handleCloseMenu();
    };

    const handleEdit = () => {
        // console.log("Edit clicked for", menuRow);
        setShowConfirmButton(false);
        if (!menuRow) return;
        const raw = menuRow.__raw || menuRow;
        if (onEditShipment) onEditShipment(raw);
        handleCloseMenu();
    };

    const handleConfirm = () => {

        if (!menuRow) return;
        const raw = menuRow.__raw || menuRow;
        if (typeof onConfirmDelivery === "function") {
            setShowConfirmButton(true);
            onConfirmDelivery(raw, { __viewOnly: true, __openConfirmDelivery: true });
        } else {
            toast.info("Confirm Delivery handler not implemented in parent.");
        }
        handleCloseMenu();
    };

    return (
        <>
            <CommonDataTable
                allPossibleColumns={allPossibleColumns}
                initialVisible={["__actions", ...defaultVisibleFields.filter(f => !EXCLUDED_FIELDS.has(f))]}
                initialRows={rows}
                height={500}
                rowsPerPageOptions={[10, 25, 50, 100]}
                exportImportPath="shipment"
                checkboxSelection={true}
                // Pass create button props to integrate with CommonDataTable
                showCreateButton={showCreateButton}
                createButtonText={createButtonText}
                createButtonIcon={createButtonIcon}
                onCreateClick={onCreateClick}
                // Add table refresh functionality
                // onDataRefresh={onDataRefresh || loadList}
                sampleFileName="shipment"
                forInventory={true}
                showSyncWms
                page={page}
                pageSize={pageSize}
                rowCount={totalCount}
                onPageChange={(newPage) => setPage(newPage)}
                onPageSizeChange={(newSize) => {
                    setPageSize(newSize);
                    setPage(0);
                }}


                onDataRefresh={async ({ filters = [], page: p = 0, pageSize: ps = pageSize } = {}) => {
                    // NOTE: this just forwards the values to your fetchHandler. For now we console.log as requested.
                    console.log("Shipping ....................... received onDataRefresh:", { filters, page: p, pageSize: ps });
                    // call your existing fetchHandler (we'll update it to accept filters)
                    // await loadList(p, ps, filters);
                    await loadList(p, ps, filters);
                }}

            />

            <Menu
                open={Boolean(menuAnchorEl)}
                anchorEl={menuAnchorEl}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <MenuItem onClick={handleView}>
                    <i className="bi bi-eye me-2"></i>View</MenuItem>
                {isInternalSource(menuRow) &&
                    !["in transit", "delivered"].includes(
                        String(menuRow?.status ?? "").trim().toLowerCase()
                    ) && (
                        <MenuItem onClick={handleEdit}>
                            <i className="bi bi-pencil me-2"></i>
                            Edit
                        </MenuItem>
                    )
                }

            </Menu>
        </>
    );
}



// <MenuItem onClick={handleConfirm}>
//     <i className="bi bi-check-circle me-2"></i>
//     Confirm Delivery</MenuItem>